import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Modal } from 'react-bootstrap';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import Loader from 'components/Loader/Loader';

import {
  addInternalNote,
  updateInternalNote,
  deleteInternalNote,
} from 'redux/modules/clientATS';
import { setSuccessMessage } from 'redux/modules/successMessage';
import { trackEvent } from 'helpers/Analytics';

import styles from './InternalNotesTab.module.scss';

const InternalNotesTab = ({ internalNotes, freelancer }) => {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNoteText, setEditedNoteText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const textareaRef = useRef(null);
  const editTextareaRef = useRef(null);
  const dispatch = useDispatch();
  const {
    loadingInternalNotes,
    addingInternalNote,
    updatingInternalNote,
    deletingInternalNote,
  } = useSelector((state) => state.clientATS);

  // Focus textarea when form opens
  useEffect(() => {
    if (isAddingNote && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isAddingNote]);

  // Focus textarea when editing a note
  useEffect(() => {
    if (editingNoteId && editTextareaRef.current) {
      editTextareaRef.current.focus();
    }
  }, [editingNoteId]);

  const handleAddNote = () => {
    if (newNote.trim() && freelancer?.id) {
      dispatch(addInternalNote(freelancer.id, newNote)).then(() => {
        trackEvent('client-creates-internal-note');
        setNewNote('');
        setIsAddingNote(false);
      });
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditedNoteText(note.note);
  };

  const handleSaveEdit = () => {
    if (editedNoteText.trim() && freelancer?.id && editingNoteId) {
      dispatch(
        updateInternalNote(freelancer.id, editingNoteId, editedNoteText)
      ).then(() => {
        trackEvent('client-updates-internal-note');
        setEditingNoteId(null);
        setEditedNoteText('');
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditedNoteText('');
  };

  const handleDeleteNote = (note) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (noteToDelete && freelancer?.id) {
      dispatch(deleteInternalNote(freelancer.id, noteToDelete.id)).then(() => {
        trackEvent('client-deletes-internal-note');
        dispatch(setSuccessMessage('Internal note deleted successfully'));
        setShowDeleteModal(false);
        setNoteToDelete(null);
      });
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setNoteToDelete(null);
  };

  if (loadingInternalNotes) {
    return <Loader loading />;
  }

  return (
    <div className={styles.container}>
      {/* Add Note Button and Form */}
      {!isAddingNote ? (
        <button
          onClick={() => setIsAddingNote(true)}
          className={styles.addNote}
        >
          <Plus size={18} />
          Add Internal Note
        </button>
      ) : (
        <div className={styles.card}>
          <textarea
            ref={textareaRef}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a private note about this applicant..."
            rows={4}
            className={styles.noteTextArea}
          />
          <div className={styles.addNoteButtonsContainer}>
            <Button
              onClick={() => {
                setIsAddingNote(false);
                setNewNote('');
              }}
              className={styles.addNoteButtons}
            >
              Cancel
            </Button>
            <ButtonWithLoader
              bsStyle="primary"
              onClick={handleAddNote}
              className={styles.addNoteButtons}
              text={addingInternalNote ? 'Saving...' : 'Save Note'}
              loading={addingInternalNote}
              disabled={!newNote.trim() || addingInternalNote}
            />
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className={styles.notesList}>
        {internalNotes.length === 0 ? (
          <div className={styles.noNotesContainer}>
            <p>No internal notes yet</p>
            <p className={styles.noNotesSubtitle}>
              Add notes to keep track of your thoughts about this candidate
            </p>
          </div>
        ) : (
          internalNotes.map((note) => (
            <div key={note.id} className={styles.card}>
              {editingNoteId === note.id ? (
                // Edit mode
                <>
                  <div className={styles.noteHeading}>
                    <div className={styles.noteAuthorSection}>
                      <Thumbnail
                        thumbnails={note?.client?.avatars}
                        className={styles.noteAuthorAvatar}
                        circle
                      />
                      <div className={styles.noteAuthor}>
                        <p className={styles.noteAuthorName}>
                          {note?.client?.displayname}
                        </p>
                        <p className={styles.noteTimestamp}>
                          {moment(note?.created_at).calendar(null, {
                            sameDay: '[Today at] hh:mm A',
                            lastDay: '[Yesterday at] hh:mm A',
                            lastWeek: 'MMMM Do [at] hh:mm A',
                            sameElse: 'MMMM Do, YYYY [at] hh:mm A',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                  <textarea
                    ref={editTextareaRef}
                    value={editedNoteText}
                    onChange={(e) => setEditedNoteText(e.target.value)}
                    rows={4}
                    className={styles.noteTextArea}
                  />
                  <div className={styles.addNoteButtonsContainer}>
                    <Button
                      onClick={handleCancelEdit}
                      className={styles.addNoteButtons}
                      disabled={updatingInternalNote}
                    >
                      Cancel
                    </Button>
                    <ButtonWithLoader
                      bsStyle="primary"
                      onClick={handleSaveEdit}
                      className={styles.addNoteButtons}
                      text={updatingInternalNote ? 'Saving...' : 'Save Note'}
                      loading={updatingInternalNote}
                      disabled={!editedNoteText.trim() || updatingInternalNote}
                    />
                  </div>
                </>
              ) : (
                // View mode
                <>
                  <div className={styles.noteHeading}>
                    <div className={styles.noteAuthorSection}>
                      <Thumbnail
                        thumbnails={note?.client?.avatars}
                        className={styles.noteAuthorAvatar}
                        circle
                      />
                      <div className={styles.noteAuthor}>
                        <p className={styles.noteAuthorName}>
                          {note?.client?.displayname}
                        </p>
                        <p className={styles.noteTimestamp}>
                          {moment(note?.created_at).calendar(null, {
                            sameDay: '[Today at] hh:mm A',
                            lastDay: '[Yesterday at] hh:mm A',
                            lastWeek: 'MMMM Do [at] hh:mm A',
                            sameElse: 'MMMM Do, YYYY [at] hh:mm A',
                          })}
                        </p>
                      </div>
                    </div>
                    <div className={styles.noteActions}>
                      <button
                        onClick={() => handleEditNote(note)}
                        className={styles.editButton}
                        aria-label="Edit note"
                        disabled={deletingInternalNote}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note)}
                        className={styles.deleteButton}
                        aria-label="Delete note"
                        disabled={deletingInternalNote}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <p className={styles.noteContent}>{note?.note}</p>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={cancelDelete}
        dialogClassName={styles.deleteModal}
      >
        <Modal.Header closeButton className={styles.deleteModalHeader}>
          <Modal.Title className={styles.deleteModalTitle}>
            Delete Internal Note
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.deleteModalBody}>
          <div className={styles.deleteModalContent}>
            <p>
              Are you sure you want to delete this internal note? This action
              cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.deleteModalFooter}>
          <Button
            onClick={cancelDelete}
            disabled={deletingInternalNote}
            className={styles.deleteNoteButtons}
          >
            Cancel
          </Button>
          <ButtonWithLoader
            bsStyle="danger"
            onClick={confirmDelete}
            text={deletingInternalNote ? 'Deleting...' : 'Delete Note'}
            loading={deletingInternalNote}
            disabled={deletingInternalNote}
            className={styles.deleteNoteButtons}
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

InternalNotesTab.propTypes = {
  internalNotes: PropTypes.array,
  freelancer: PropTypes.object,
};

export default InternalNotesTab;
